import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Award, ShieldCheck, Sparkles, Filter, Thermometer, Droplets, Lightbulb,
  Gem, Dna, Fish, PenTool, RefreshCw, CalendarCheck, FlaskConical, Wrench,
  ArrowRight, ArrowLeft, ChevronRight,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";

const TRANSITION_DURATION_MS = 1500;

const WHY = [
  { icon: Award, title: "Premier Saltwater Specialists", body: "Designing and maintaining custom saltwater aquariums across Phoenix and the surrounding Valley with years of professional experience." },
  { icon: ShieldCheck, title: "Built for Long-Term Success", body: "Every aquarium begins with a carefully engineered foundation that promotes healthy livestock and lasting stability." },
  { icon: Sparkles, title: "Luxury Aquarium Care", body: "From initial installation to ongoing maintenance, Seatech provides complete white-glove aquarium services tailored to every client." },
];

const STEPS = [
  { icon: Filter, title: "Professional Filtration", body: "Mechanical, biological, and chemical filtration systems are carefully selected to ensure exceptional water quality." },
  { icon: Thermometer, title: "Climate Control", body: "Temperature stability is maintained using premium heaters, chillers, and monitoring equipment designed for marine environments." },
  { icon: Droplets, title: "Pure RODI Water", body: "Every aquarium begins with purified RODI water and professional-grade conditioners to create ideal water chemistry." },
  { icon: Lightbulb, title: "Professional Lighting", body: "Lighting systems are selected to support coral health while enhancing the natural beauty of the aquarium." },
  { icon: Gem, title: "Live Rock Aquascaping", body: "Beautiful live rock formations are carefully crafted to create natural habitats with depth, movement, and biological filtration." },
  { icon: Dna, title: "Tank Cycling", body: "The aquarium is patiently cycled until beneficial bacteria establish healthy biological filtration and water parameters are stable enough for marine life." },
  { icon: Fish, title: "Livestock Introduction", body: "Colorful fish and invertebrates are introduced gradually using carefully monitored acclimation procedures." },
];

const SERVICES = [
  { icon: PenTool, title: "Custom Aquarium Design", body: "Design completely new saltwater aquariums tailored to homes, offices, restaurants, and commercial environments." },
  { icon: RefreshCw, title: "Aquarium Redesign", body: "Transform existing aquariums with updated aquascaping, equipment, livestock selection, and modern aesthetics." },
  { icon: CalendarCheck, title: "Aquarium Maintenance", body: "Scheduled maintenance programs keep every system healthy, balanced, and beautiful year-round." },
  { icon: Fish, title: "Livestock Selection", body: "Professional guidance when selecting compatible fish, corals, and invertebrates for long-term success." },
  { icon: FlaskConical, title: "Water Quality Management", body: "RODI water production, water chemistry balancing, additives, and ongoing testing." },
  { icon: Wrench, title: "Equipment Installation", body: "Professional installation of pumps, filtration systems, lighting, controllers, skimmers, and monitoring equipment." },
];

const GALLERY = [
  "https://images.unsplash.com/photo-1533713692156-f70938dc0d54?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1546026423-cc4642628d2b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1599492816933-2101fe60bc72?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1514907283155-ea5f4094c70c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.pexels.com/photos/9004341/pexels-photo-9004341.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/9004340/pexels-photo-9004340.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 44, filter: "blur(6px)" }}
    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Bubbles = () => {
  const bubbles = useMemo(
    () =>
      Array.from({ length: 16 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 6 + Math.random() * 22,
        duration: 10 + Math.random() * 12,
        delay: Math.random() * 10,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {bubbles.map((b) => (
        <motion.span
          key={b.id}
          initial={{ y: "110%", opacity: 0 }}
          animate={{ y: "-15%", opacity: [0, 0.5, 0.5, 0] }}
          transition={{ duration: b.duration, delay: b.delay, repeat: Infinity, ease: "linear" }}
          className="absolute rounded-full border border-[var(--aqua)]/30 bg-[var(--aqua)]/10 backdrop-blur-[1px]"
          style={{ left: `${b.left}%`, width: b.size, height: b.size }}
        />
      ))}
    </div>
  );
};

export default function SaltwaterAquariums() {
  const [phase, setPhase] = useState("intro");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, []);

  useEffect(() => {
    if (phase !== "intro") return;
    const t = setTimeout(() => setPhase("page"), TRANSITION_DURATION_MS);
    return () => clearTimeout(t);
  }, [phase]);

  const goHome = (target) => {
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector(target);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { duration: 1.4 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 250);
  };

  return (
    <main data-testid="saltwater-page" className="relative min-h-screen bg-[var(--ink)] text-[var(--foam)]">
      {/* Cinematic intro transition */}
      {phase === "intro" && (
        <div className="fixed inset-0 z-[70] bg-[var(--ink)] cursor-pointer" data-testid="saltwater-intro" onClick={() => setPhase("page")}>
          <video
            className="absolute inset-0 h-full w-full object-cover"
            src="/media/saltwater-transition.mp4"
            autoPlay
            muted
            playsInline
            onEnded={() => setPhase("page")}
            data-testid="saltwater-transition-video"
          />
        </div>
      )}

      {/* Fixed looping background */}
      <div className="fixed inset-0 z-0">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src="/media/saltwater-loop.mp4"
          autoPlay
          loop
          muted
          playsInline
          data-testid="saltwater-loop-video"
        />
        <div className="absolute inset-0 bg-[#0a0d20]/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090b1c]/85 via-[#0d1026]/72 to-[#081222]/94" />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: phase === "page" ? 1 : 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10"
      >
        <Navbar />

        {/* HERO */}
        <section className="relative flex min-h-[58vh] items-center overflow-hidden px-6 lg:px-16 pt-52 pb-10">
          <Bubbles />
          <div className="relative mx-auto flex w-full max-w-4xl flex-col items-center text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-[var(--aqua)]/40 bg-[var(--aqua)]/10 px-4 py-2 font-body text-xs font-medium uppercase tracking-[0.28em] text-[var(--aqua)]" data-testid="saltwater-kicker">
                Custom Marine Installations
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-title text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl" data-testid="saltwater-headline">
                Custom Saltwater<br />Aquariums
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl font-body text-base font-light leading-relaxed text-[var(--foam-dim)] lg:text-lg">
                Professionally designed, expertly maintained, and built for long-term success. From
                custom installations to ongoing care, Seatech creates thriving marine ecosystems
                throughout the Phoenix Valley.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => goHome("#contact")}
                  data-testid="saltwater-hero-cta"
                  className="group inline-flex items-center gap-3 rounded-full bg-[var(--aqua)] px-8 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[var(--ink-900)] transition-all duration-300 hover:shadow-[0_0_40px_-8px_var(--aqua)]"
                >
                  Schedule a Consultation
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button
                  onClick={() => document.getElementById("process")?.scrollIntoView({ behavior: "smooth" })}
                  data-testid="saltwater-hero-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--foam)]/25 px-6 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[var(--foam)]/90 backdrop-blur-md transition-all duration-300 hover:border-[var(--foam)]/60"
                >
                  Explore Our Process
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* WHY SEATECH */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Why Seatech</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {WHY.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.12}>
                  <div
                    className="group h-full rounded-3xl border border-[var(--foam)]/12 bg-[#0b1024]/45 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[var(--aqua)]/40"
                    data-testid={`saltwater-why-card-${i}`}
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[var(--aqua)]/25 bg-[var(--aqua)]/10 text-[var(--aqua)] transition-all duration-500 group-hover:shadow-[0_0_30px_-6px_var(--aqua)]">
                      <c.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-title text-xl font-bold tracking-tight">{c.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{c.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* THE SEATECH STANDARD */}
        <section id="process" className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-5xl">
            <Reveal>
              <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[var(--aqua)]">Our Process</span>
              <h2 className="mt-3 font-title text-3xl font-bold tracking-tight lg:text-4xl">The Seatech Standard</h2>
            </Reveal>
            <div className="relative mt-14">
              <div className="absolute left-7 top-0 bottom-0 hidden w-px bg-gradient-to-b from-[var(--aqua)]/50 via-[var(--foam)]/15 to-transparent md:block" />
              <div className="space-y-6">
                {STEPS.map((s, i) => (
                  <Reveal key={s.title} delay={i * 0.05}>
                    <div
                      className="group relative flex items-start gap-6 rounded-3xl border border-[var(--foam)]/12 bg-[#0b1024]/45 p-6 backdrop-blur-2xl transition-all duration-500 hover:border-[var(--aqua)]/40 md:pl-8"
                      data-testid={`saltwater-step-${i}`}
                    >
                      <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[var(--aqua)]/30 bg-[var(--aqua)]/10 text-[var(--aqua)] transition-all duration-500 group-hover:shadow-[0_0_30px_-6px_var(--aqua)]">
                        <s.icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                      <div>
                        <span className="font-body text-xs font-medium uppercase tracking-[0.24em] text-[var(--aqua)]">Step {i + 1}</span>
                        <h3 className="mt-1 font-title text-lg font-bold tracking-tight">{s.title}</h3>
                        <p className="mt-2 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{s.body}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CUSTOM AQUARIUM SERVICES */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Custom Aquarium Services</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {SERVICES.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div
                    className="group relative h-full overflow-hidden rounded-3xl border border-[var(--foam)]/12 bg-[#0b1024]/45 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[var(--aqua)]/40 hover:shadow-[0_20px_60px_-30px_var(--aqua)]"
                    data-testid={`saltwater-service-${i}`}
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[var(--aqua)]/25 bg-[var(--aqua)]/10 text-[var(--aqua)]">
                      <s.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-title text-lg font-bold tracking-tight">{s.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[var(--foam-dim)]">{s.body}</p>
                    <ChevronRight className="mt-5 h-5 w-5 text-[var(--aqua)] opacity-0 transition-all duration-500 group-hover:translate-x-1 group-hover:opacity-100" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* BEAUTIFUL MARINE ECOSYSTEMS */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl">Every Aquarium Is Unique</h2>
              <p className="mt-5 max-w-2xl font-body text-sm font-light leading-relaxed text-[var(--foam-dim)] lg:text-base">
                From minimalist reef displays to vibrant coral ecosystems, every aquarium is designed
                to complement your space while supporting healthy marine life.
              </p>
            </Reveal>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {GALLERY.map((src, i) => (
                <Reveal key={i} delay={(i % 3) * 0.1}>
                  <div
                    className="group relative aspect-[4/3] overflow-hidden rounded-3xl border border-[var(--foam)]/12"
                    data-testid={`saltwater-gallery-${i}`}
                  >
                    <img
                      src={src}
                      alt="Custom saltwater aquarium"
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#090b1c]/70 via-transparent to-transparent" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative px-6 lg:px-16 py-32">
          <div className="absolute inset-0 bg-[#060814]/70" />
          <div className="relative mx-auto max-w-3xl text-center">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl" data-testid="saltwater-final-heading">
                Ready to Build Your Dream Saltwater Aquarium?
              </h2>
              <p className="mx-auto mt-6 max-w-2xl font-body text-sm font-light leading-relaxed text-[var(--foam-dim)] lg:text-base">
                Whether you're creating your first reef aquarium or upgrading an existing system,
                Seatech Aquariums provides expert design, installation, maintenance, and long-term
                support to ensure your aquarium thrives for years to come.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => goHome("#contact")}
                  data-testid="saltwater-final-cta"
                  className="inline-flex items-center gap-3 rounded-full bg-[var(--aqua)] px-9 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[var(--ink-900)] transition-all duration-300 hover:shadow-[0_0_44px_-8px_var(--aqua)]"
                >
                  Book a Consultation
                </button>
                <button
                  onClick={() => goHome("#about")}
                  data-testid="saltwater-final-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--foam)]/25 px-8 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[var(--foam)]/90 backdrop-blur-md transition-all duration-300 hover:border-[var(--foam)]/60"
                >
                  View Maintenance Programs
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      </motion.div>
    </main>
  );
}
